"use client";

import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { ArrowRight, Calendar, User, Clock } from "lucide-react";
import { SectionContainer } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { TextGradient } from "@/components/ui/text-gradient";
import { cn, formatDate } from "@/lib/utils";

interface NewsArticle {
  title: string;
  excerpt: string;
  imageSrc: string;
  date: Date;
  author: string;
  readingTime: number;
  category: string;
}

export function NewsSection() {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const newsArticles: NewsArticle[] = [
    {
      title: "Blue Lego Bricks Show 42% Increase in Creative Project Usage",
      excerpt: "Our latest research shows that blue brick incorporation in creative projects has risen significantly over the past year, indicating a positive shift in builder preferences.",
      imageSrc: "https://images.unsplash.com/photo-1575650681837-c0ca3e270de1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
      date: new Date(2023, 8, 15), // Sept 15, 2023
      author: "Research Team",
      readingTime: 4,
      category: "Research",
    },
    {
      title: "Blue Brick Rescue Initiative Collects Over 500,000 Pieces",
      excerpt: "Our nationwide brick rescue program has successfully collected and redistributed half a million blue pieces to schools, clubs, and underprivileged communities.",
      imageSrc: "https://images.unsplash.com/photo-1587654780291-39c9404d746b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
      date: new Date(2023, 9, 3), // Oct 3, 2023
      author: "Jordan Reynolds",
      readingTime: 5,
      category: "Program Update",
    },
    {
      title: "Annual 'Build Blue' Competition Announces Winners",
      excerpt: "This year's competition saw record participation with over 3,000 entries showcasing extraordinary blue brick creations from builders around the world.",
      imageSrc: "https://images.unsplash.com/photo-1632058097246-b09b113a9b66?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
      date: new Date(2023, 10, 22), // Nov 22, 2023
      author: "Marcus Chen",
      readingTime: 3,
      category: "Events",
    },
  ];

  return (
    <SectionContainer id="news" className="bg-white">
      <SectionHeading
        title={
          <>
            Latest <TextGradient text="News" />
          </>
        }
        subtitle="Stay updated with the latest developments, research, and stories from Blue Legos Matter."
      />

      <div
        ref={ref}
        className="grid grid-cols-1 md:grid-cols-3 gap-8"
      >
        {newsArticles.map((article, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
          >
            <Card className="overflow-hidden h-full group cursor-pointer hover:shadow-md transition-shadow duration-300">
              <div className="relative overflow-hidden">
                <div className="aspect-[16/9]">
                  <img 
                    src={article.imageSrc} 
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="absolute top-4 left-4">
                  <Badge variant="blue">{article.category}</Badge>
                </div>
              </div>
              
              <CardContent className="p-6">
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>{formatDate(article.date)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" />
                    <span>{article.author}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{article.readingTime} min read</span>
                  </div>
                </div>
                
                <h3 className="font-heading text-xl font-bold mb-2 leading-tight">
                  {article.title}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {article.excerpt}
                </p>
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    className="p-0 h-auto font-medium text-blue-medium hover:text-blue-dark group/btn"
                  >
                    Read More{" "}
                    <ArrowRight className="h-4 w-4 ml-1 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <Button variant="blue-outline" size="lg">
          View All News
        </Button>
      </div>
    </SectionContainer>
  );
}